/** @format */

import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { getApplicationStageName } from '../../../../common/Logic/commonLogic';
import { ApplicationDetailsDto } from '../../Models';

interface Props {
  applicant: ApplicationDetailsDto | null;
}
export default function Side({ applicant }: Props) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const getGender = (key: number | string) => {
    switch (key) {
      case 1:
        return 'Male';
      case 2:
        return 'Female';
      default:
        return 'Other';
    }
  };

  const getRace = (key: number | string) => {
    switch (key) {
      case 1:
        return 'African';
      case 2:
        return 'White';
      case 3:
        return 'Indian';
      case 4:
        return 'Coloured';
      default:
        return 'Unspecified';
    }
  };

  if (!applicant?.personalDetails) return null;

  const pd = applicant.personalDetails;
  const initials = `${pd.name?.charAt(0) ?? ''}${pd.surname?.charAt(0) ?? ''}`;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'visible',
        background: (t) =>
          t.palette.mode === 'light'
            ? `linear-gradient(135deg, ${t.palette.common.white} 0%, ${t.palette.grey[50]} 100%)`
            : t.palette.background.paper,
      }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
          alignItems={{ xs: 'center', sm: 'flex-start' }}>
          <Avatar
            sx={{
              width: { xs: 88, md: 104 },
              height: { xs: 88, md: 104 },
              fontSize: { xs: 32, md: 36 },
              fontWeight: 700,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: (t) =>
                `0 8px 24px ${t.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.35)' : 'rgba(0,0,0,0.4)'}`,
            }}>
            {initials}
          </Avatar>

          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography
              variant='h4'
              component='h1'
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', md: '2rem' },
                lineHeight: 1.2,
                mb: 1,
              }}>
              {`${pd.name} ${pd.surname}`}
            </Typography>

            <Stack
              direction='row'
              flexWrap='wrap'
              gap={1}
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              sx={{ mb: 1.5 }}>
              <Chip
                color='success'
                size='small'
                sx={{ fontWeight: 600 }}
                label={`${getApplicationStageName(applicant.applicationStage)}${
                  applicant.universityOfChoice
                    ? ` · ${applicant.universityOfChoice}`
                    : ''
                }`}
              />
            </Stack>

            {applicant.applicationStage === 12 && isMdUp && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (t) =>
                    t.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.06)'
                      : 'action.hover',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  maxWidth: 640,
                  mx: { xs: 'auto', sm: 0 },
                }}>
                <Typography variant='body2' color='text.secondary' component='div'>
                  Dear {`${pd.name} ${pd.surname}`},<br />
                  <br />
                  Kindly note that even though your application meets Thuthuka&apos;s
                  minimum requirements, this <strong>does not mean that you have been
                  accepted for funding by Thuthuka yet</strong>. Selection is based on
                  each university&apos;s specific criteria and available funding.
                  <br />
                  <br />
                  Please keep checking your emails for further communication regarding the
                  status of your application.
                  <br />
                  <br />
                  Kind Regards,
                </Typography>
              </Box>
            )}

            <Stack
              direction='row'
              spacing={1}
              flexWrap='wrap'
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              sx={{ color: 'text.secondary', mb: 2 }}>
              <Typography variant='body2'>{pd.identityNumber}</Typography>
              <Typography variant='body2' color='text.disabled'>
                ·
              </Typography>
              <Typography variant='body2'>{getGender(pd.gender)}</Typography>
              <Typography variant='body2' color='text.disabled'>
                ·
              </Typography>
              <Typography variant='body2'>{getRace(pd.race)}</Typography>
            </Stack>

            <Stack
              spacing={1.25}
              alignItems={{ xs: 'center', sm: 'flex-start' }}
              sx={{ mt: 1 }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <AlternateEmailIcon
                  fontSize='small'
                  sx={{ color: 'primary.main', opacity: 0.9 }}
                />
                <Link
                  href={`mailto:${pd.email}`}
                  underline='hover'
                  variant='body2'
                  sx={{ wordBreak: 'break-all' }}>
                  {pd.email}
                </Link>
              </Stack>
              <Stack direction='row' spacing={1} alignItems='center'>
                <PhoneAndroidIcon
                  fontSize='small'
                  sx={{ color: 'primary.main', opacity: 0.9 }}
                />
                <Link
                  href={`tel:${pd.phoneNumber}`}
                  underline='hover'
                  variant='body2'>
                  {pd.phoneNumber}
                </Link>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        {applicant.applicationStage === 12 && !isMdUp && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: (t) =>
                t.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.06)' : 'action.hover',
              borderLeft: '4px solid',
              borderColor: 'primary.main',
            }}>
            <Typography variant='body2' color='text.secondary' component='div'>
              Dear {`${pd.name} ${pd.surname}`},<br />
              <br />
              Kindly note that even though your application meets Thuthuka&apos;s minimum
              requirements, this <strong>does not mean that you have been accepted for
              funding by Thuthuka yet</strong>. Selection is based on each
              university&apos;s specific criteria and available funding.
              <br />
              <br />
              Please keep checking your emails for further communication regarding the
              status of your application.
              <br />
              <br />
              Kind Regards,
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
